'use client'

import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import type { HoldingsData } from '@/features/portfolio'
import { Dash } from '@/ui/atoms/dash/Dash'
import { Tooltip, TooltipContentLong, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Sparkles, WalletIcon } from 'lucide-react'

const HOLDINGS_DUST_THRESHOLD = 0.01

export function formatHoldingsUsd(value: number | undefined): string {
  if (!value || value === 0) return '-'
  return USD_MOCK_TOKEN.formatUSD(NormalizedUnitNumber(value), { compact: true })
}

export function HoldingsCellBase({ positionUsd, walletUsd, subAccountDeposits, walletBreakdown }: HoldingsData) {
  const grandTotal = (positionUsd ?? 0) + (walletUsd ?? 0)

  if (grandTotal === 0) {
    return <Dash />
  }

  return (
    <Tooltip>
      <TooltipTrigger
        asChild
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <span className="cursor-help font-medium text-foreground text-sm">{formatHoldingsUsd(grandTotal)}</span>
      </TooltipTrigger>
      <TooltipContentLong>
        <div className="flex min-w-56 flex-col gap-4">
          {positionUsd !== undefined && positionUsd > 0 && (
            <div className="flex flex-col gap-1">
              <div className="mb-1 flex items-center justify-between gap-6">
                <div className="flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-[#FBE572]" />
                  <Typography variant="h4" className="text-sm leading-none">
                    Earning
                  </Typography>
                </div>
                <Typography className="font-bold leading-none">{formatHoldingsUsd(positionUsd)}</Typography>
              </div>
              {subAccountDeposits?.map((sub) => (
                <div key={sub.subAccountId} className="ml-5 flex items-center justify-between gap-6">
                  <span className="text-muted-foreground text-xs">Position {sub.subAccountId}</span>
                  <span className="text-muted-foreground text-xs">{formatHoldingsUsd(sub.depositUsd)}</span>
                </div>
              ))}
            </div>
          )}
          {walletUsd !== undefined && walletUsd > 0 && (
            <div className="flex flex-col gap-1">
              <div className="mb-1 flex items-center justify-between gap-6">
                <div className="flex items-center gap-1.5">
                  <WalletIcon className="h-3.5 w-3.5 text-[#A1FCE7]" />
                  <Typography variant="h4" className="text-sm leading-none">
                    Wallet
                  </Typography>
                </div>
                <Typography className="font-bold leading-none">{formatHoldingsUsd(walletUsd)}</Typography>
              </div>
              {walletBreakdown && (
                <div className="ml-5">
                  {walletBreakdown.nativeUsd > HOLDINGS_DUST_THRESHOLD && (
                    <div className="flex items-center justify-between gap-6">
                      <span className="text-muted-foreground text-xs">HYPE</span>
                      <span className="text-muted-foreground text-xs">
                        {formatHoldingsUsd(walletBreakdown.nativeUsd)}
                      </span>
                    </div>
                  )}
                  {walletBreakdown.wrappedUsd > HOLDINGS_DUST_THRESHOLD && (
                    <div className="flex items-center justify-between gap-6">
                      <span className="text-muted-foreground text-xs">WHYPE</span>
                      <span className="text-muted-foreground text-xs">
                        {formatHoldingsUsd(walletBreakdown.wrappedUsd)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </TooltipContentLong>
    </Tooltip>
  )
}
